import React, { useEffect, useMemo, useState } from "react";
import { db, auth } from "../firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  getDocs,
  orderBy,
} from "firebase/firestore";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Paper,
  Stack,
} from "@mui/material";
import moment from "moment";

function Kotcancels() {
  const [propertyId, setPropertyId] = useState(null);
  const [logs, setLogs] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [search, setSearch] = useState("");

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  /* ================= GET PROPERTY FROM AUTHROLE ================= */
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) return;

      const roleSnap = await getDoc(doc(db, "authroles", user.uid));
      if (!roleSnap.exists()) return;

      setPropertyId(roleSnap.data().propertyId);
    });

    return () => unsub();
  }, []);

  /* ================= FETCH USERS ================= */
  useEffect(() => {
    if (!propertyId) return;

    const fetchUsers = async () => {
      const q = query(
        collection(db, "authroles"),
        where("propertyId", "==", propertyId)
      );

      const snap = await getDocs(q);

      const map = {};
      snap.forEach((doc) => {
        map[doc.id] = doc.data();
      });

      setUsersMap(map);
    };

    fetchUsers();
  }, [propertyId]);

  /* ================= FETCH CANCEL LOGS ================= */
  useEffect(() => {
    if (!propertyId) return;

    const q = query(
      collection(db, "kotcancellogs"),
      where("propertyId", "==", propertyId),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setLogs(data);
    });

    return () => unsub();
  }, [propertyId]);

  /* ================= FILTERED DATA ================= */
  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      const user = usersMap[log.userid];

      const searchText = `${log.name || ""} ${log.note || ""} ${
        user?.name || user?.email || ""
      }`
        .toLowerCase()
        .trim();

      return searchText.includes(search.toLowerCase());
    });
  }, [logs, usersMap, search]);

  /* ================= PAGINATED DATA ================= */
  const paginatedLogs = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredLogs.slice(start, start + rowsPerPage);
  }, [filteredLogs, page, rowsPerPage]);

  /* ================= HANDLERS ================= */
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box className=" p-3">
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" mb={2}>
            <Typography variant="h6">KOT Cancel Logs</Typography>
          </Stack>

          <TextField
            size="small"
            placeholder="Search item / user / reason..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{ mb: 2 }}
          />

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell align="center">Qty</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedLogs.length > 0 ? (
                  paginatedLogs.map((log, index) => {
                    const user = usersMap[log.userid];

                    return (
                      <TableRow key={log.id}>
                        <TableCell>
                          {page * rowsPerPage + index + 1}
                        </TableCell>

                        <TableCell>{log.name}</TableCell>

                        <TableCell
                          align="center"
                          sx={{ color: "error.main", fontWeight: 600 }}
                        >
                          {log.qty}
                        </TableCell>

                        <TableCell>{log.note}</TableCell>

                        <TableCell>
                          {user?.name || user?.email || "Unknown"}
                        </TableCell>

                        <TableCell>
                          {log.createdAt?.toDate
                            ? moment(log.createdAt.toDate()).format(
                                "DD-MM-YYYY HH:mm"
                              )
                            : ""}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No cancel logs found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={filteredLogs.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}

export default Kotcancels;